import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Globe, TrendingUp, ArrowUpDown, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Info, BarChart3, DollarSign, Heart, ArrowRight, Search } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useBrand } from "@/config/brands";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CountryInfo {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  currency_code: string;
  healthcare_system: string;
  default_coverage: string;
  fee_schedule_name: string;
}

interface CountryProcedureDetail {
  us_code: string;
  name: string;
  intl_price: number;
  us_gross: number;
  multiplier: number;
}

interface CountryStats {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  currency_code: string;
  healthcare_system: string;
  default_coverage: string;
  fee_schedule_name: string;
  procedure_count: number;
  avg_multiplier: number;
  median_multiplier: number;
  avg_intl_price: number;
  avg_us_gross: number;
  procedures: CountryProcedureDetail[];
}

interface ProcedureComparison {
  us_code: string;
  procedure_name: string;
  us_gross: number;
  prices: Record<string, number | null>;
  country_count: number;
}

interface CategoryBreakdown {
  country_code: string;
  category: string;
  procedure_count: number;
  avg_multiplier: number;
  median_multiplier: number;
}

// ─── Exchange rates (for display) ────────────────────────────────────────────
const EXCHANGE_RATES: Record<string, number> = {
  AUD: 0.64, EUR: 1.08, CAD: 0.72, GBP: 1.27, ILS: 0.28,
  JPY: 0.0067, KRW: 0.00074, CHF: 1.13,
};

// ─── Category classification ─────────────────────────────────────────────────
function classifyProcedure(code: string): string {
  const num = parseInt(code, 10);
  if (isNaN(num)) return 'Other';
  if (num >= 10000 && num <= 19999) return 'Surgery';
  if (num >= 20000 && num <= 29999) return 'Musculoskeletal';
  if (num >= 30000 && num <= 39999) return 'Respiratory/Cardiovascular';
  if (num >= 40000 && num <= 49999) return 'Digestive';
  if (num >= 50000 && num <= 59999) return 'Urinary/Reproductive';
  if (num >= 60000 && num <= 69999) return 'Nervous System';
  if (num >= 70000 && num <= 79999) return 'Radiology';
  if (num >= 80000 && num <= 89999) return 'Pathology/Lab';
  if (num >= 90000 && num <= 99999) return 'Medicine/E&M';
  return 'Other';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatUSD(amount: number): string {
  if (amount >= 10000) return `$${(amount / 1000).toFixed(0)}k`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  if (amount >= 1) return `$${Math.round(amount).toLocaleString()}`;
  return `$${amount.toFixed(2)}`;
}

function formatMultiplier(m: number): string {
  if (m >= 100) return `${Math.round(m)}x`;
  if (m >= 10) return `${m.toFixed(0)}x`;
  return `${m.toFixed(1)}x`;
}

function multiplierColor(m: number): string {
  // Higher multiplier = US charges way more = great savings abroad = green
  if (m >= 30) return 'text-green-400';
  if (m >= 15) return 'text-emerald-400';
  if (m >= 8) return 'text-yellow-400';
  if (m >= 4) return 'text-orange-400';
  return 'text-red-400';
}

function multiplierBg(m: number): string {
  // Higher multiplier = better deal abroad = green
  if (m >= 30) return 'bg-green-500/20 border-green-500/40';
  if (m >= 15) return 'bg-emerald-500/20 border-emerald-500/40';
  if (m >= 8) return 'bg-yellow-500/20 border-yellow-500/40';
  if (m >= 4) return 'bg-orange-500/20 border-orange-500/40';
  return 'bg-red-500/20 border-red-500/40';
}

// Country sort order (by multiplier, most egregious first)
const COUNTRY_ORDER = ['DE', 'JP', 'KR', 'FR', 'GB', 'CA', 'AU', 'CH', 'IL'];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function InternationalComparison() {
  const brand = useBrand();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [tableSortField, setTableSortField] = useState<string>('us_gross');
  const [tableSortDir, setTableSortDir] = useState<'asc' | 'desc'>('desc');
  const [cardSearch, setCardSearch] = useState<Record<string, string>>({});
  const [cardPage, setCardPage] = useState<Record<string, number>>({});
  const [showAllProcedures, setShowAllProcedures] = useState(false);
  const CARD_PAGE_SIZE = 10;

  // ── Fetch countries ──────────────────────────────────────────────────────
  const { data: countries } = useQuery({
    queryKey: ['international-countries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('international_countries')
        .select('country_code, country_name, flag_emoji, currency_code, healthcare_system, default_coverage, fee_schedule_name')
        .neq('country_code', 'US')
        .order('country_name');
      if (error) throw error;
      return data as CountryInfo[];
    },
  });

  // ── Fetch all international prices with US pricing joined ────────────────
  const { data: rawPrices, isLoading } = useQuery({
    queryKey: ['international-comparison-prices'],
    queryFn: async () => {
      // Helper: fetch all rows with pagination (PostgREST defaults to 1000)
      async function fetchAll<T>(query: any): Promise<T[]> {
        const PAGE = 1000;
        let all: T[] = [];
        let offset = 0;
        while (true) {
          const { data, error } = await query.range(offset, offset + PAGE - 1);
          if (error) throw error;
          if (!data || data.length === 0) break;
          all = all.concat(data as T[]);
          if (data.length < PAGE) break;
          offset += PAGE;
        }
        return all;
      }

      // Helper: chunked .in() to avoid URL length limits
      async function fetchInChunks<T>(
        table: string,
        selectCols: string,
        filterCol: string,
        values: string[],
        extraFilters?: (q: any) => any
      ): Promise<T[]> {
        const CHUNK = 200;
        const results: T[] = [];
        for (let i = 0; i < values.length; i += CHUNK) {
          const chunk = values.slice(i, i + CHUNK);
          let q = supabase.from(table).select(selectCols).in(filterCol, chunk);
          if (extraFilters) q = extraFilters(q);
          const { data, error } = await q.limit(CHUNK * 2);
          if (error) throw error;
          if (data) results.push(...(data as T[]));
        }
        return results;
      }

      // Get all international prices (paginated)
      const prices = await fetchAll<{ us_code: string; country_code: string; price_usd: number }>(
        supabase.from('international_procedure_prices')
          .select('us_code, country_code, price_usd')
          .gt('price_usd', 0)
      );

      // Get unique codes
      const uniqueCodes = [...new Set(prices.map(p => p.us_code))];

      // Fetch US pricing and procedure names in chunks
      const [usPricing, procNames] = await Promise.all([
        fetchInChunks<{ cpt_code: string; avg_gross_charge: number; median_gross_charge: number }>(
          'cpt_pricing_averages',
          'cpt_code, avg_gross_charge, median_gross_charge',
          'cpt_code',
          uniqueCodes,
          (q: any) => q.eq('setting_type', 'all_settings')
        ),
        fetchInChunks<{ code: string; llm_quick_name: string }>(
          'procedures',
          'code, llm_quick_name',
          'code',
          uniqueCodes
        ),
      ]);

      return { prices, usPricing, procNames };
    },
  });

  // ── Compute country stats ────────────────────────────────────────────────
  const countryStats = useMemo<CountryStats[]>(() => {
    if (!rawPrices || !countries) return [];

    const usMap = new Map(rawPrices.usPricing.map(u => [u.cpt_code, u]));
    const nameMap = new Map(rawPrices.procNames.map(p => [p.code, p.llm_quick_name]));
    const countryMap = new Map(countries.map(c => [c.country_code, c]));

    const statsMap: Record<string, { multipliers: number[]; intlPrices: number[]; usGross: number[]; procedures: CountryProcedureDetail[] }> = {};

    for (const p of rawPrices.prices) {
      const us = usMap.get(p.us_code);
      if (!us || !us.avg_gross_charge || us.avg_gross_charge <= 0) continue;
      if (!p.price_usd || p.price_usd <= 0) continue;

      const mult = Number(us.avg_gross_charge) / Number(p.price_usd);
      if (!isFinite(mult) || mult <= 0) continue;

      if (!statsMap[p.country_code]) {
        statsMap[p.country_code] = { multipliers: [], intlPrices: [], usGross: [], procedures: [] };
      }
      statsMap[p.country_code].multipliers.push(mult);
      statsMap[p.country_code].intlPrices.push(Number(p.price_usd));
      statsMap[p.country_code].usGross.push(Number(us.avg_gross_charge));
      statsMap[p.country_code].procedures.push({
        us_code: p.us_code,
        name: nameMap.get(p.us_code) || p.us_code,
        intl_price: Number(p.price_usd),
        us_gross: Number(us.avg_gross_charge),
        multiplier: mult,
      });
    }

    return Object.entries(statsMap)
      .map(([cc, s]) => {
        const ci = countryMap.get(cc);
        if (!ci) return null;
        const sorted = [...s.multipliers].sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        // Sort procedures by multiplier descending (biggest US overcharges first)
        s.procedures.sort((a, b) => b.multiplier - a.multiplier);
        return {
          country_code: cc,
          country_name: ci.country_name,
          flag_emoji: ci.flag_emoji,
          currency_code: ci.currency_code,
          healthcare_system: ci.healthcare_system || '',
          default_coverage: ci.default_coverage || '',
          fee_schedule_name: ci.fee_schedule_name || '',
          procedure_count: s.multipliers.length,
          avg_multiplier: s.multipliers.reduce((a, b) => a + b, 0) / s.multipliers.length,
          median_multiplier: median,
          avg_intl_price: s.intlPrices.reduce((a, b) => a + b, 0) / s.intlPrices.length,
          avg_us_gross: s.usGross.reduce((a, b) => a + b, 0) / s.usGross.length,
          procedures: s.procedures,
        } as CountryStats;
      })
      .filter(Boolean)
      .sort((a, b) => b!.avg_multiplier - a!.avg_multiplier) as CountryStats[];
  }, [rawPrices, countries]);

  // ── Compute procedure comparison table ───────────────────────────────────
  const procedureComparisons = useMemo<ProcedureComparison[]>(() => {
    if (!rawPrices) return [];

    const usMap = new Map(rawPrices.usPricing.map(u => [u.cpt_code, u]));
    const nameMap = new Map(rawPrices.procNames.map(p => [p.code, p.llm_quick_name]));

    // Group prices by us_code
    const grouped: Record<string, { prices: Record<string, number>; us_gross: number; name: string }> = {};

    for (const p of rawPrices.prices) {
      const us = usMap.get(p.us_code);
      if (!us || !us.avg_gross_charge || us.avg_gross_charge <= 0) continue;
      if (!p.price_usd || p.price_usd <= 0) continue;

      if (!grouped[p.us_code]) {
        grouped[p.us_code] = {
          prices: {},
          us_gross: Number(us.avg_gross_charge),
          name: nameMap.get(p.us_code) || p.us_code,
        };
      }
      // Keep highest price per country for this code (some have multiple settings)
      const existing = grouped[p.us_code].prices[p.country_code];
      if (!existing || Number(p.price_usd) > existing) {
        grouped[p.us_code].prices[p.country_code] = Number(p.price_usd);
      }
    }

    return Object.entries(grouped)
      .map(([code, data]) => ({
        us_code: code,
        procedure_name: data.name,
        us_gross: data.us_gross,
        prices: data.prices,
        country_count: Object.keys(data.prices).length,
      }))
      .filter(p => p.country_count >= 2); // At least 2 countries for comparison
  }, [rawPrices]);

  // ── Category breakdown ───────────────────────────────────────────────────
  const categoryBreakdown = useMemo<CategoryBreakdown[]>(() => {
    if (!rawPrices) return [];

    const usMap = new Map(rawPrices.usPricing.map(u => [u.cpt_code, u]));
    const buckets: Record<string, Record<string, number[]>> = {};

    for (const p of rawPrices.prices) {
      const us = usMap.get(p.us_code);
      if (!us || !us.avg_gross_charge || us.avg_gross_charge <= 0) continue;
      if (!p.price_usd || p.price_usd <= 0) continue;

      const mult = Number(us.avg_gross_charge) / Number(p.price_usd);
      if (!isFinite(mult) || mult <= 0) continue;

      const cat = classifyProcedure(p.us_code);
      const key = `${p.country_code}::${cat}`;
      if (!buckets[key]) buckets[key] = { multipliers: [] };
      (buckets[key] as any).cc = p.country_code;
      (buckets[key] as any).cat = cat;
      buckets[key].multipliers.push(mult);
    }

    return Object.values(buckets)
      .filter((b: any) => b.multipliers.length >= 3)
      .map((b: any) => {
        const sorted = [...b.multipliers].sort((a: number, b: number) => a - b);
        return {
          country_code: b.cc,
          category: b.cat,
          procedure_count: b.multipliers.length,
          avg_multiplier: b.multipliers.reduce((a: number, c: number) => a + c, 0) / b.multipliers.length,
          median_multiplier: sorted[Math.floor(sorted.length / 2)],
        };
      })
      .sort((a, b) => b.median_multiplier - a.median_multiplier);
  }, [rawPrices]);

  // ── Filter & sort procedure table ────────────────────────────────────────
  const filteredProcedures = useMemo(() => {
    let filtered = procedureComparisons;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => classifyProcedure(p.us_code) === selectedCategory);
    }

    return [...filtered].sort((a, b) => {
      let va: number, vb: number;
      if (tableSortField === 'us_gross') {
        va = a.us_gross; vb = b.us_gross;
      } else if (tableSortField === 'countries') {
        va = a.country_count; vb = b.country_count;
      } else if (tableSortField === 'name') {
        return tableSortDir === 'asc'
          ? a.procedure_name.localeCompare(b.procedure_name)
          : b.procedure_name.localeCompare(a.procedure_name);
      } else {
        // Sort by a specific country's price
        va = a.prices[tableSortField] || 0;
        vb = b.prices[tableSortField] || 0;
      }
      return tableSortDir === 'asc' ? va - vb : vb - va;
    });
  }, [procedureComparisons, selectedCategory, tableSortField, tableSortDir]);

  const displayedProcedures = showAllProcedures ? filteredProcedures : filteredProcedures.slice(0, 25);

  // ── Aggregate headline stats ─────────────────────────────────────────────
  const headlineStats = useMemo(() => {
    if (!countryStats.length) return null;
    const totalProcedures = countryStats.reduce((s, c) => s + c.procedure_count, 0);
    const overallAvg = countryStats.reduce((s, c) => s + c.avg_multiplier * c.procedure_count, 0) / totalProcedures;
    const mostExpensiveCategory = categoryBreakdown.length > 0 ? categoryBreakdown[0] : null;
    return { totalProcedures, overallAvg, mostExpensiveCategory, countryCount: countryStats.length };
  }, [countryStats, categoryBreakdown]);

  // ── Available categories ─────────────────────────────────────────────────
  const categories = useMemo(() => {
    const cats = new Set(procedureComparisons.map(p => classifyProcedure(p.us_code)));
    return ['all', ...Array.from(cats).sort()];
  }, [procedureComparisons]);

  // ── Sort handler ─────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (tableSortField === field) {
      setTableSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setTableSortField(field);
      setTableSortDir('desc');
    }
  };

  // ── Active countries in the data ─────────────────────────────────────────
  const activeCountries = useMemo(() => {
    return COUNTRY_ORDER.filter(cc => countryStats.some(c => c.country_code === cc));
  }, [countryStats]);

  // ── Category colors (inline styles to avoid Tailwind purge issues) ───
  const categoryColors: Record<string, { bg: string; text: string }> = {
    'Surgery': { bg: '#dc2626', text: '#ffffff' },
    'Musculoskeletal': { bg: '#ea580c', text: '#ffffff' },
    'Respiratory/Cardiovascular': { bg: '#db2777', text: '#ffffff' },
    'Digestive': { bg: '#d97706', text: '#ffffff' },
    'Urinary/Reproductive': { bg: '#9333ea', text: '#ffffff' },
    'Nervous System': { bg: '#2563eb', text: '#ffffff' },
    'Radiology': { bg: '#0e7490', text: '#ffffff' },
    'Pathology/Lab': { bg: '#059669', text: '#ffffff' },
    'Medicine/E&M': { bg: '#0d9488', text: '#ffffff' },
    'Other': { bg: '#4b5563', text: '#ffffff' },
  };

  // ── Full-page loading skeleton ───────────────────────────────────────────
  const isAhp = brand.design === 'civic';
  if (isLoading) {
    const skBg = isAhp ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)';
    const skBgStrong = isAhp ? 'rgba(var(--civic-gold-rgb),0.12)' : 'rgba(0,255,65,0.08)';
    const skBgRed = 'rgba(239,68,68,0.15)';
    const pageBg = isAhp ? 'var(--civic-bg-dark)' : 'hsl(var(--background))';
    const accentColor = isAhp ? 'var(--civic-gold)' : '#00ff41';
    return (
      <div className="min-h-screen pb-24" style={{ background: pageBg }}>
        <style>{`
          @keyframes intlShimmer {
            0% { background-position: -600px 0; }
            100% { background-position: 600px 0; }
          }
          .intl-shimmer {
            background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.07) 50%, transparent 100%);
            background-size: 600px 100%;
            animation: intlShimmer 1.6s ease-in-out infinite;
          }
        `}</style>

        {/* Hero skeleton */}
        <div className="relative border-b border-white/10 px-4 sm:px-6 py-8 sm:py-12" style={{ background: isAhp ? 'rgba(var(--civic-bg-dark-rgb),0.6)' : 'transparent' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full intl-shimmer" style={{ background: skBgStrong }} />
            <div className="h-8 w-72 rounded-lg intl-shimmer" style={{ background: skBgStrong }} />
          </div>
          <div className="h-4 w-96 rounded intl-shimmer mb-2" style={{ background: skBg }} />
          <div className="h-4 w-64 rounded intl-shimmer" style={{ background: skBg }} />
          <div className="mt-6 flex flex-wrap gap-4">
            {[{ w: 160, bg: skBgRed }, { w: 140, bg: skBgStrong }, { w: 150, bg: 'rgba(245,158,11,0.15)' }].map((s, i) => (
              <div key={i} className="rounded-lg px-5 py-3 intl-shimmer" style={{ width: s.w, height: 80, background: s.bg, animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        </div>

        <div className="w-full px-4 sm:px-6 py-6 space-y-8">
          {/* Country cards skeleton */}
          <section>
            <div className="h-5 w-64 rounded intl-shimmer mb-4" style={{ background: skBgStrong }} />
            <div className="h-3 w-96 rounded intl-shimmer mb-4" style={{ background: skBg }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-white/10 p-4 space-y-3 intl-shimmer" style={{ height: 220, background: skBg, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
          </section>

          {/* Heatmap skeleton */}
          <section>
            <div className="h-5 w-72 rounded intl-shimmer mb-4" style={{ background: skBgRed }} />
            <div className="h-3 w-80 rounded intl-shimmer mb-4" style={{ background: skBg }} />
            <div className="space-y-1.5">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="flex gap-1">
                  <div className="h-8 w-36 rounded intl-shimmer" style={{ background: skBg, animationDelay: `${i * 50}ms` }} />
                  {Array.from({ length: 7 }).map((_, j) => (
                    <div key={j} className="h-8 flex-1 rounded intl-shimmer" style={{ background: skBgRed, opacity: 0.4 + Math.random() * 0.6, animationDelay: `${(i * 7 + j) * 30}ms` }} />
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* Table skeleton */}
          <section>
            <div className="h-5 w-56 rounded intl-shimmer mb-4" style={{ background: 'rgba(245,158,11,0.2)' }} />
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="grid grid-cols-10 gap-3 px-4 py-3 border-b border-white/10">
                {[3, 1, 1, 1, 1, 1, 1, 1].map((span, i) => (
                  <div key={i} className={`col-span-${span} h-4 rounded intl-shimmer`} style={{ background: skBg, animationDelay: `${i * 60}ms` }} />
                ))}
              </div>
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="grid grid-cols-10 gap-3 px-4 py-4 border-b border-white/5">
                  <div className="col-span-3 h-6 rounded intl-shimmer" style={{ background: skBg, animationDelay: `${i * 40}ms` }} />
                  <div className="col-span-1 h-6 rounded intl-shimmer" style={{ background: skBgRed, animationDelay: `${i * 40 + 60}ms` }} />
                  {Array.from({ length: 6 }).map((_, j) => (
                    <div key={j} className="col-span-1 h-6 rounded intl-shimmer" style={{ background: skBg, opacity: 0.5 + j * 0.08, animationDelay: `${i * 40 + j * 30 + 90}ms` }} />
                  ))}
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Animated loading indicator */}
        <div className="py-4 flex flex-col items-center gap-3">
          <div className="flex gap-2">
            {[0, 160, 320].map((delay) => (
              <div key={delay} className="w-2 h-2 rounded-full animate-bounce"
                style={{ background: accentColor, animationDelay: `${delay}ms` }} />
            ))}
          </div>
          <p className="font-mono text-sm" style={{ color: isAhp ? 'rgba(var(--civic-cream-rgb),0.4)' : 'rgba(0,255,65,0.6)' }}>
            Loading international pricing data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <Helmet>
        <title>US vs International Healthcare Prices | {brand.name}</title>
        <meta name="description" content="Compare US hospital prices with Australia, UK, Canada, France, Germany, Japan, and more. See how much Americans overpay for the same procedures compared to other developed nations." />
        <meta name="keywords" content="international healthcare prices, US healthcare comparison, global medical costs, healthcare price comparison, US vs UK healthcare, US vs Canada healthcare" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/international`} />
        <meta property="og:title" content="US vs International Healthcare Prices" />
        <meta property="og:description" content="Compare US hospital prices with Australia, UK, Canada, France, Germany, Japan, and more. See how much Americans overpay for the same procedures compared to other developed nations." />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/international`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="US vs International Healthcare Prices" />
        <meta name="twitter:description" content="Compare US hospital prices with Australia, UK, Canada, France, Germany, Japan, and more. See how much Americans overpay for the same procedures compared to other developed nations." />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>

      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden border-b border-matrix-green/20">
        <div className="absolute inset-0 bg-gradient-to-br from-matrix-green/5 via-background to-blood-red/5" />
        <div className="relative w-full px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-8 h-8 text-matrix-green" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              International Price Comparison
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl text-sm sm:text-base">
            How US hospital sticker prices compare to government fee schedules in {countryStats.length || '...'} countries.
            Based on {headlineStats ? headlineStats.totalProcedures.toLocaleString() : '...'} procedure-country price comparisons.
          </p>

          {/* Headline stat */}
          {headlineStats && (
            <div className="mt-6 flex flex-wrap gap-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-5 py-3">
                <div className="text-xs text-red-400 uppercase tracking-wider font-medium">Avg US Markup</div>
                <div className="text-2xl sm:text-3xl font-black text-red-400 tabular-nums">
                  {formatMultiplier(headlineStats.overallAvg)}
                </div>
                <div className="text-xs text-muted-foreground">higher than other countries</div>
              </div>
              <div className="bg-matrix-green/10 border border-matrix-green/30 rounded-lg px-5 py-3">
                <div className="text-xs text-matrix-green uppercase tracking-wider font-medium">Countries Compared</div>
                <div className="text-2xl sm:text-3xl font-black text-matrix-green tabular-nums">
                  {headlineStats.countryCount}
                </div>
                <div className="text-xs text-muted-foreground">national fee schedules</div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-5 py-3">
                <div className="text-xs text-amber-400 uppercase tracking-wider font-medium">Procedures Mapped</div>
                <div className="text-2xl sm:text-3xl font-black text-amber-400 tabular-nums">
                  {procedureComparisons.length.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">unique CPT codes</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 py-6 space-y-8">

        {/* ── Country Cards ────────────────────────────────────────────────── */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-matrix-green" />
            Country-by-Country: US Price Multiplier
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            How many times more US hospitals charge compared to each country's regulated fee schedule.
            Sorted by average multiplier (most overpriced first).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {countryStats.map(cs => {
                const search = (cardSearch[cs.country_code] || '').toLowerCase();
                const page = cardPage[cs.country_code] || 0;
                const filtered = search
                  ? cs.procedures.filter(p => p.name.toLowerCase().includes(search) || p.us_code.includes(search))
                  : cs.procedures;
                const totalPages = Math.ceil(filtered.length / CARD_PAGE_SIZE);
                const pageProcs = filtered.slice(page * CARD_PAGE_SIZE, (page + 1) * CARD_PAGE_SIZE);

                return (
                <Card
                  key={cs.country_code}
                  className={`border ${multiplierBg(cs.avg_multiplier)}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{cs.flag_emoji}</span>
                        <div>
                          <CardTitle className="text-base">{cs.country_name}</CardTitle>
                          <p className="text-xs text-muted-foreground">{cs.fee_schedule_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-muted-foreground">US charges</div>
                        <div className={`text-2xl font-black tabular-nums ${multiplierColor(cs.avg_multiplier)}`}>
                          {formatMultiplier(cs.avg_multiplier)}
                        </div>
                        <div className="text-[10px] text-muted-foreground">more</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {/* Stats row */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>System: <span className="text-foreground font-medium">{cs.healthcare_system}</span></span>
                      <span>Coverage: <span className="text-foreground font-medium">{cs.default_coverage}</span></span>
                      <span>Currency: <span className="text-foreground font-medium">{cs.currency_code}</span></span>
                    </div>

                    {/* Stats summary */}
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="bg-muted/30 rounded-md py-1.5 px-1">
                        <div className="text-[10px] text-muted-foreground">Average</div>
                        <div className={`text-sm font-bold ${multiplierColor(cs.avg_multiplier)}`}>{formatMultiplier(cs.avg_multiplier)}</div>
                      </div>
                      <div className="bg-muted/30 rounded-md py-1.5 px-1">
                        <div className="text-[10px] text-muted-foreground">Median</div>
                        <div className="text-sm font-bold text-foreground">{formatMultiplier(cs.median_multiplier)}</div>
                      </div>
                      <div className="bg-muted/30 rounded-md py-1.5 px-1">
                        <div className="text-[10px] text-muted-foreground">Avg Intl</div>
                        <div className="text-sm font-bold text-foreground">{formatUSD(cs.avg_intl_price)}</div>
                      </div>
                      <div className="bg-muted/30 rounded-md py-1.5 px-1">
                        <div className="text-[10px] text-muted-foreground">Avg US</div>
                        <div className="text-sm font-bold text-red-400">{formatUSD(cs.avg_us_gross)}</div>
                      </div>
                    </div>

                    {/* Procedure list header + search */}
                    <div className="border-t border-border/40 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                          {filtered.length} procedures {search && `matching "${search}"`}
                        </div>
                      </div>
                      <div className="relative mb-2">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search procedures..."
                          className="w-full pl-6 pr-2 py-1 text-xs bg-muted/30 border border-border/30 rounded-md focus:outline-none focus:ring-1 focus:ring-primary/50"
                          value={cardSearch[cs.country_code] || ''}
                          onChange={e => {
                            setCardSearch(prev => ({ ...prev, [cs.country_code]: e.target.value }));
                            setCardPage(prev => ({ ...prev, [cs.country_code]: 0 }));
                          }}
                        />
                      </div>

                      {/* Procedure rows */}
                      <div className="space-y-0.5 text-xs">
                        {pageProcs.map((proc, i) => (
                          <div key={proc.us_code} className="flex items-center gap-2 py-1 border-b border-border/10 last:border-0">
                            <span className="text-muted-foreground w-5 text-right shrink-0">{page * CARD_PAGE_SIZE + i + 1}.</span>
                            <span className="flex-1 truncate" title={proc.name}>{proc.name}</span>
                            <span className="text-muted-foreground tabular-nums shrink-0">{formatUSD(proc.intl_price)}</span>
                            <span className="text-muted-foreground shrink-0">→</span>
                            <span className="text-red-400 tabular-nums shrink-0">{formatUSD(proc.us_gross)}</span>
                            <span
                              className="font-bold tabular-nums min-w-[42px] text-right shrink-0"
                              style={{ color: proc.multiplier >= 15 ? '#22c55e' : proc.multiplier >= 4 ? '#eab308' : '#ef4444' }}
                            >
                              {formatMultiplier(proc.multiplier)}
                            </span>
                          </div>
                        ))}
                        {filtered.length === 0 && (
                          <div className="text-center text-muted-foreground py-2">No matching procedures</div>
                        )}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/20">
                          <button
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 flex items-center gap-0.5"
                            disabled={page === 0}
                            onClick={() => setCardPage(prev => ({ ...prev, [cs.country_code]: page - 1 }))}
                          >
                            <ChevronLeft className="w-3 h-3" /> Prev
                          </button>
                          <span className="text-[10px] text-muted-foreground">
                            {page + 1} / {totalPages}
                          </span>
                          <button
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 flex items-center gap-0.5"
                            disabled={page >= totalPages - 1}
                            onClick={() => setCardPage(prev => ({ ...prev, [cs.country_code]: page + 1 }))}
                          >
                            Next <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
        </section>

        {/* ── Category Heatmap ─────────────────────────────────────────────── */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-red-400" />
            Category Breakdown: Where the US Overcharges Most
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Median US price multiplier by medical category and country. Darker = more overpriced.
          </p>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="min-w-[700px] px-4 sm:px-0">
              {/* Header row */}
              <div className="grid gap-px" style={{ gridTemplateColumns: `160px repeat(${activeCountries.length}, 1fr)` }}>
                <div className="text-xs font-medium text-muted-foreground p-2">Category</div>
                {activeCountries.map(cc => {
                  const cs = countryStats.find(c => c.country_code === cc);
                  return (
                    <div key={cc} className="text-center text-xs font-medium text-muted-foreground p-2">
                      {cs?.flag_emoji} {cc}
                    </div>
                  );
                })}
              </div>

              {/* Data rows */}
              {['Pathology/Lab', 'Radiology', 'Medicine/E&M', 'Surgery', 'Digestive', 'Respiratory/Cardiovascular', 'Musculoskeletal', 'Nervous System', 'Urinary/Reproductive'].map(cat => (
                <div
                  key={cat}
                  className="grid gap-px border-t border-border/20"
                  style={{ gridTemplateColumns: `160px repeat(${activeCountries.length}, 1fr)` }}
                >
                  <div className="text-xs p-2 flex items-center">
                    <span
                      className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold"
                      style={{
                        backgroundColor: (categoryColors[cat] || categoryColors['Other']).bg,
                        color: (categoryColors[cat] || categoryColors['Other']).text,
                      }}
                    >
                      {cat}
                    </span>
                  </div>
                  {activeCountries.map(cc => {
                    const cell = categoryBreakdown.find(b => b.country_code === cc && b.category === cat);
                    if (!cell) return <div key={cc} className="p-2 text-center text-xs text-muted-foreground/40">--</div>;

                    // Heatmap: red (low multiplier = less savings) → green (high multiplier = great savings)
                    const intensity = Math.min(cell.median_multiplier / 60, 1);
                    let r: number, g: number, b: number;
                    if (intensity < 0.5) {
                      // Red to yellow (low savings → moderate)
                      const t = intensity * 2;
                      r = Math.round(220 - 80 * t);
                      g = Math.round(60 + 100 * t);
                      b = 30;
                    } else {
                      // Yellow to green (moderate → great savings)
                      const t = (intensity - 0.5) * 2;
                      r = Math.round(140 * (1 - t) + 30);
                      g = Math.round(160);
                      b = Math.round(30 + 40 * t);
                    }

                    return (
                      <TooltipProvider key={cc}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className="p-2 text-center text-xs font-mono font-bold rounded-sm cursor-default"
                              style={{
                                backgroundColor: `rgba(${r}, ${g}, ${b}, 0.3)`,
                                color: `rgb(${Math.min(r + 30, 255)}, ${Math.min(g + 30, 255)}, ${Math.min(b + 30, 255)})`,
                              }}
                            >
                              {formatMultiplier(cell.median_multiplier)}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium">{cat} in {countryStats.find(c => c.country_code === cc)?.country_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Median: {formatMultiplier(cell.median_multiplier)} | Avg: {formatMultiplier(cell.avg_multiplier)} | {cell.procedure_count} procedures
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Procedure Comparison Table ────────────────────────────────────── */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-400" />
                Procedure-Level Price Comparison
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {filteredProcedures.length.toLocaleString()} procedures with prices in 2+ countries. Click column headers to sort.
              </p>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0 border border-border/30 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead
                    className="cursor-pointer select-none sticky left-0 bg-background z-10 min-w-[200px]"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Procedure
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right min-w-[90px]"
                    onClick={() => handleSort('us_gross')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      US Avg
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  {activeCountries.map(cc => {
                    const cs = countryStats.find(c => c.country_code === cc);
                    return (
                      <TableHead
                        key={cc}
                        className="cursor-pointer select-none text-right min-w-[80px]"
                        onClick={() => handleSort(cc)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {cs?.flag_emoji} {cc}
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedProcedures.map(proc => (
                  <TableRow key={proc.us_code} className="hover:bg-muted/20">
                    <TableCell className="sticky left-0 bg-background z-10 font-medium">
                      <Link
                        to={`/cpt/${proc.us_code}`}
                        className="hover:text-matrix-green transition-colors"
                      >
                        <div className="text-sm leading-tight">{proc.procedure_name}</div>
                        <div className="text-[10px] text-muted-foreground">CPT {proc.us_code}</div>
                      </Link>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-red-400 font-bold">
                      {formatUSD(proc.us_gross)}
                    </TableCell>
                    {activeCountries.map(cc => {
                      const price = proc.prices[cc];
                      if (!price) return <TableCell key={cc} className="text-right text-xs text-muted-foreground/30">--</TableCell>;

                      const mult = proc.us_gross / price;
                      return (
                        <TableCell key={cc} className="text-right">
                          <div className="text-sm font-mono">{formatUSD(price)}</div>
                          <div className={`text-[10px] font-medium ${multiplierColor(mult)}`}>
                            {formatMultiplier(mult)}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredProcedures.length > 25 && !showAllProcedures && (
            <div className="text-center mt-4">
              <Button variant="outline" onClick={() => setShowAllProcedures(true)}>
                Show All {filteredProcedures.length.toLocaleString()} Procedures
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
          {showAllProcedures && filteredProcedures.length > 25 && (
            <div className="text-center mt-4">
              <Button variant="outline" onClick={() => setShowAllProcedures(false)}>
                Show Top 25 Only
                <ChevronUp className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </section>

        {/* ── Spotlight: Most Overpriced Procedures ─────────────────────────── */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-blood-red" />
            Most Overpriced: Common Procedures You Know
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {procedureComparisons
              .filter(p => p.country_count >= 4)
              .sort((a, b) => b.us_gross - a.us_gross)
              .slice(0, 6)
              .map(proc => {
                const avgIntl = Object.values(proc.prices).filter(Boolean).reduce((s, p) => s + (p || 0), 0) / Object.values(proc.prices).filter(Boolean).length;
                const mult = proc.us_gross / avgIntl;

                return (
                  <Card key={proc.us_code} className="border border-border/40 hover:border-matrix-green/40 transition-colors">
                    <CardContent className="pt-4 pb-3">
                      <Link to={`/cpt/${proc.us_code}`} className="hover:text-matrix-green transition-colors">
                        <h3 className="font-semibold text-sm mb-1">{proc.procedure_name}</h3>
                      </Link>
                      <p className="text-[10px] text-muted-foreground mb-3">CPT {proc.us_code} | {proc.country_count} countries</p>

                      <div className="flex items-end justify-between mb-3">
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase">US Average</div>
                          <div className="text-xl font-black text-red-400 tabular-nums">{formatUSD(proc.us_gross)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-muted-foreground uppercase">Global Avg</div>
                          <div className="text-lg font-bold text-green-400 tabular-nums">{formatUSD(avgIntl)}</div>
                        </div>
                      </div>

                      <div className={`text-center py-1.5 rounded text-xs font-bold ${multiplierBg(mult)} ${multiplierColor(mult)}`}>
                        US charges {formatMultiplier(mult)} more
                      </div>

                      {/* Country price pills */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {Object.entries(proc.prices)
                          .filter(([, p]) => p)
                          .sort(([, a], [, b]) => (a || 0) - (b || 0))
                          .map(([cc, price]) => {
                            const cs = countryStats.find(c => c.country_code === cc);
                            return (
                              <span key={cc} className="inline-flex items-center gap-1 text-[10px] bg-muted/30 px-1.5 py-0.5 rounded-full">
                                <span>{cs?.flag_emoji}</span>
                                <span className="font-mono">{formatUSD(price!)}</span>
                              </span>
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </section>

        {/* ── Methodology Note ─────────────────────────────────────────────── */}
        <Card className="border-border/30 bg-muted/10">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground space-y-2">
                <p className="font-medium text-foreground/80">About this comparison</p>
                <p>
                  <strong>US prices</strong> are average gross charges (sticker prices) from hospital chargemasters across {'>'}5,000 US hospitals.
                  These represent the "list price" before insurance negotiation.
                </p>
                <p>
                  <strong>International prices</strong> are from official government fee schedules:
                  Australia (MBS), France (CCAM), UK (NHS), Japan (Shinryo Houshu), Israel (MOH Tariff),
                  Canada (OHIP), Germany (EBM), South Korea (HIRA), and Switzerland (TARMED).
                  These represent what the healthcare system pays providers.
                </p>
                <p>
                  Multipliers show how many times higher US gross charges are compared to each country's regulated price.
                  Actual patient out-of-pocket costs vary by insurance coverage in both the US and internationally.
                </p>
                <Link to="/data-methodology" className="inline-flex items-center gap-1 text-matrix-green hover:underline mt-1">
                  Full methodology <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
